import {React, useContext, Redirect, useMemo} from 'external/react'
import {styled, Box, TableContainer, Table} from 'external/material'
import { parseOrderColumn } from 'helpers/url'
import { getGroupGrades, getGroupMembers, filterGradeColumnsByPeriod, filterStudentsByPattern, getStudentGrades, sortStudentGrades, getSelectedPeriod } from 'helpers/gradebook'

import InfoBar from './InfoBar'
import GradeFilters from './Filters'
import GradesHead from './Head'
import GradesBody from './Body'
import DeleteExternalGrade from './DeleteExternalGrade'


import {TeacherContext} from 'teacher/context'
import {Loader} from 'components/loader'

const StyledGradebook = styled(Box)(gradebookStyles)

export default function GradeBook() {

    const context = useContext(TeacherContext);

    const {query, groupId, groups, navigate} = context
    const {items: periods} = context.rollbookContextPeriods
  
    const selectedPeriod = useMemo(
      () => getSelectedPeriod(periods, query.periodId),
      [periods, query.periodId]
    )

    const orderBy = useMemo(
      () => parseOrderColumn(query.orderBy, 'student'),
      [query.orderBy]
    )

    const findStudentGrades = useMemo(
      () => getGroupGrades(context.rollbookContextGrades.items),
      [context.rollbookContextGrades.items]
    )
    const groupMembers = useMemo(
      () => getGroupMembers(groupId, groups),
      [groups, groupId]
    )

    const columnsFilteredByPeriod = useMemo(
      () => filterGradeColumnsByPeriod(context.rollbookContextGradeColumns.items, selectedPeriod),
      [context.rollbookContextGradeColumns.items, selectedPeriod]
    )

    const filteredStudents = useMemo(
      () => filterStudentsByPattern(groupMembers, query.pattern),
      [groupMembers, query.pattern]
    )

    const studentGrades = useMemo(
      () => getStudentGrades(filteredStudents, columnsFilteredByPeriod, findStudentGrades, context.rollbookContextStudentAssignmentProperties),
      [filteredStudents, columnsFilteredByPeriod, findStudentGrades, context.rollbookContextStudentAssignmentProperties]
    )

    const sortedStudents = useMemo(
      () => sortStudentGrades(orderBy, studentGrades),
      [orderBy, studentGrades]
    )

    const createSortHandler = useMemo(() =>
      (property) => () => navigate({groupId}, undefined, {
        preserveQuery: true,
        query: {orderBy: `${orderBy.orderColumn == property && orderBy.order == 'asc' ? '-' : ''}${property}`}
      }),
      [groupId, navigate, orderBy]
    )
    return (
      <StyledGradebook> 
        <InfoBar />
        <DeleteExternalGrade />
        <Box className="filters">
          <GradeFilters grades={sortedStudents} />
        </Box>
        <Box className="content">
          <TableContainer className="container">
            <Table className="table">
              <GradesHead columns={columnsFilteredByPeriod} orderBy={orderBy} createSortHandler={createSortHandler} />
              <GradesBody rows={sortedStudents} columns={columnsFilteredByPeriod}/>
            </Table>
          </TableContainer>
        </Box>
      </StyledGradebook>
    )
}

export function RedirectToFirstGroup() {
  const {groups, groupsLoaded, link} = useContext(TeacherContext)
  const [firstGroup] = groups
  const to = firstGroup ? link({groupId: firstGroup.id}) : link({page: 'dashboard'}, '/:languageId/:role/:page')
  return groupsLoaded
    ? <Redirect to={to}/>
    : <Loader isLoading={!groupsLoaded} />
}

function gradebookStyles ({theme}) {
  return {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    overflowY: 'hidden',
    "& >.filters": {
      display: 'flex',
      padding: '0 20px',
      paddingBottom: 0,
      [theme.breakpoints.down('xs')]: {
        padding: 0
      }
    },
    "& > .content": {
      display: 'flex',
      flexGrow: 1,
      overflow: 'hidden',
      padding: 20,
      paddingTop: 0,
      overflowY: 'auto',
      [theme.breakpoints.down('xs')]: {
        padding: 0
      },
      "& > .container": {
        flexGrow: 1,
        overflow: 'auto',
        border: 'solid 1px #d8dbe0',
        borderTop: 0,
        [theme.breakpoints.down('xs')]: {
          border: 0
        }
      },
    },
  }
}
